import { useSyncExternalStore } from "react";
import { BREAKPOINTS, type BreakpointName } from "@cntrl/tokens";

/**
 * `useBreakpoint` — the current active breakpoint by min-width, driven by matchMedia. Behavior
 * parity with the MVP's collapses (the 720 → md collapse etc.), not exact pixels. SSR-safe (server snapshot
 * returns the largest breakpoint; the Razor public app never calls it anyway).
 */
const ORDER: readonly BreakpointName[] = ["sm", "md", "lg", "xl", "max"];

// One MediaQueryList per breakpoint, built once. A `matchMedia` list fires its `change` listener ONLY when
// the viewport crosses that breakpoint — never per resize event — so there is nothing to debounce (the swap
// fires the instant the boundary is crossed, ART-167's requirement) AND no `window.innerWidth` read runs on
// every resize. That read is a forced layout reflow, and mobile browsers fire `resize` continuously during
// scroll (URL-bar collapse), on keyboard show/hide, and on navigation — so a bare `resize` listener over
// `innerWidth` (the previous shape) meant one synchronous reflow per event per mounted consumer. `.matches`
// is a read of the already-evaluated query, not a reflow.
//
// `matchMedia` is universal in browsers; the `innerWidth` + `resize` fallback below exists only for
// environments that lack it (jsdom under test — the `Picker` coarse-pointer guard makes the same allowance).
const HAS_MATCH_MEDIA = typeof window !== "undefined" && typeof window.matchMedia === "function";

const QUERIES: readonly { readonly bp: BreakpointName; readonly mql: MediaQueryList }[] = HAS_MATCH_MEDIA
  ? ORDER.map((bp) => ({ bp, mql: window.matchMedia(`(min-width: ${BREAKPOINTS[bp]}px)`) }))
  : [];

function computeBreakpoint(): BreakpointName {
  if (typeof window === "undefined") return "max";
  let current: BreakpointName = "sm";
  if (HAS_MATCH_MEDIA) {
    for (const { bp, mql } of QUERIES) {
      if (mql.matches) current = bp;
    }
  } else {
    const width = window.innerWidth;
    for (const bp of ORDER) {
      if (width >= BREAKPOINTS[bp]) current = bp;
    }
  }
  return current;
}

function subscribe(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  if (!HAS_MATCH_MEDIA) {
    window.addEventListener("resize", callback);
    return () => window.removeEventListener("resize", callback);
  }
  for (const { mql } of QUERIES) mql.addEventListener("change", callback);
  return () => {
    for (const { mql } of QUERIES) mql.removeEventListener("change", callback);
  };
}

export function useBreakpoint(): BreakpointName {
  return useSyncExternalStore(subscribe, computeBreakpoint, () => "max");
}

/**
 * True when the viewport is at or above the given breakpoint (e.g. `useMinWidth("lg")` for the desktop shell).
 * REACTIVE — the returned boolean flips the instant the viewport crosses the breakpoint (`useBreakpoint` is
 * `useSyncExternalStore` over `resize`), and because it is a boolean it only re-renders its consumer at the
 * crossing, not per pixel. The shell-selection in `router.tsx` reads this to auto-swap chrome on a runtime
 * resize (ART-167). A bare `resize` + `innerWidth` debounce was tried and removed — it never settled during a
 * continuous slow drag, so the swap only fired once the window was already tiny — and its un-debounced
 * replacement reflowed on every resize event. `matchMedia` fixes both: it fires exactly at the crossing (no
 * debounce, always settles) and reads no layout.
 */
export function useMinWidth(bp: BreakpointName): boolean {
  const current = useBreakpoint();
  return ORDER.indexOf(current) >= ORDER.indexOf(bp);
}
